import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Text, Button } from '@conexasaude/conexa-hero'
import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import { useRealtimeAttendanceContext } from '@/modules/RealtimeAttendance/contexts/RealtimeAttendanceContext'
import { sendMetrics } from '@/modules/RealtimeAttendance/services'
import { useStyles } from './styles'
import { ATTENDANCE_STATES } from '@/modules/RealtimeAttendance/constants'
import CustomLayout from '@/components/CustomLayout'
import InviteToCallModal from '../../components/InviteToCallModal'
import { RenderIf } from '@/components/RenderIf'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { MeetingType } from './types'
import { mergeParams } from '@/utils/mergeParams'
import { getEnableTranscription } from '../../video-call-services'
import { CanceledByVideoErrorModal } from '@/modules/RealtimeAttendance/components/CanceledByVideoErrorModal'
import MeetManager from '../../components/MeetManager'
import { useCallAppointmentMonitoring } from '../../queries/useCallMonitoring'
import { useCallMonitoringStore } from '../../store/call-monitoring-store'
import { useGetImmediateAttendance } from '@/modules/RealtimeAttendance/queries'
import { useAttendanceCallStore } from '@/modules/RealtimeAttendance/store/useAttendanceCall'
import { validateRulesToAccessMeet } from '@/components/HOCs/ValidateRulesToAccessMeet'

type MeetParams = {
  skipGuide?: string
  flagTranscription?: string
}

const VideoRoom: FunctionComponent = () => {
  const history = useHistory()
  const location = useLocation()

  const [sessionId, setSessionId] = useState('')
  const { realtimeAttendance, callingTimes, clearTimer } =
    useRealtimeAttendanceContext()
  const { clinic } = useAuthContext()
  const { showModal } = useModalContext()
  const styles = useStyles()
  const [meetingUrl, setMeetingUrl] = useState('')
  const [enableTranscription, setEnableTranscription] = useState(false)

  const urlSearchParams = new URLSearchParams(location.search)
  const appointmentId = urlSearchParams.get('a')

  const { refetch } = useGetImmediateAttendance(
    realtimeAttendance?.immediate?.status,
  )
  const attendanceCall = useAttendanceCallStore(store => store.attendanceCall)

  const callAppointment = useCallAppointmentMonitoring(appointmentId, false)

  const isAnymeet = attendanceCall?.meetingType === MeetingType.ANYMEET

  const handleInviteClick = () => {
    showModal('INVITE_TO_CALL_MODAL', {
      title: 'Convide alguém para a chamada',
    })
  }

  useEffect(() => {
    if (realtimeAttendance?.immediate?.status) {
      refetch()
    }
  }, [realtimeAttendance, refetch])

  useEffect(() => {
    if (!attendanceCall && !callAppointment?.data?.callId) return

    const currentCallAppointment = {
      callId: attendanceCall?.callId,
      queueStatus: attendanceCall?.queueStatus,
      meetingUrl: attendanceCall?.zoomMeetingUrl,
      sessionId: attendanceCall?.zoomMeetingId,
    }

    if (!attendanceCall) {
      currentCallAppointment.callId = callAppointment?.data?.callId
      currentCallAppointment.meetingUrl = callAppointment?.data?.meetUrl
    }

    const sendMetricsAndClearTimer = async () => {
      await sendMetrics(currentCallAppointment?.callId, {
        callingTimes,
        status: currentCallAppointment?.queueStatus,
      })

      clearTimer()
    }
    sendMetricsAndClearTimer()

    if (currentCallAppointment?.meetingUrl?.includes('express')) {
      setSessionId(currentCallAppointment?.sessionId)
      return
    }

    const urlParams = new URLSearchParams(currentCallAppointment?.meetingUrl)
    setSessionId(urlParams.get('sessionId'))
    // eslint-disable-next-line
  }, [realtimeAttendance?.immediate?.status, callAppointment?.data?.callId])

  const canInvitePeople = useMemo(() => {
    return (
      clinic?.flag?.PermiteCriacaoUrlConvidado &&
      attendanceCall?.meetingType &&
      !isAnymeet
    )
  }, [
    clinic?.flag?.PermiteCriacaoUrlConvidado,
    isAnymeet,
    attendanceCall?.meetingType,
  ])

  const generateMeetingUrl = useCallback(async () => {
    const params: MeetParams = {}

    const currentCallAppointment = {
      callId: attendanceCall?.callId,
      queueStatus: attendanceCall?.queueStatus,
      meetingUrl: attendanceCall?.zoomMeetingUrl,
      sessionId: attendanceCall?.zoomMeetingId,
      appointmentId: attendanceCall?.attendanceId,
    }

    if (!realtimeAttendance?.call && !callAppointment?.data?.callId) return

    if (!realtimeAttendance?.call) {
      currentCallAppointment.callId = callAppointment?.data?.callId
      currentCallAppointment.meetingUrl = callAppointment?.data?.meetUrl
      currentCallAppointment.appointmentId = parseInt(appointmentId)
    }

    try {
      await getEnableTranscription(currentCallAppointment.appointmentId).then(
        response => {
          if (response) {
            params.flagTranscription = 'true'
            setEnableTranscription(true)
          }
        },
      )
    } catch (err) {
      console.log('err', err)
    } finally {
      if (isAnymeet) {
        params.skipGuide = 'true'
      }

      const mergetMeetingUrl = mergeParams(
        currentCallAppointment?.meetingUrl,
        params,
      )

      setMeetingUrl(mergetMeetingUrl)
    }
  }, [
    appointmentId,
    isAnymeet,
    callAppointment,
    realtimeAttendance,
    attendanceCall,
  ])

  useEffect(() => {
    generateMeetingUrl()
  }, [generateMeetingUrl])

  useEffect(() => {
    const unblock = history.block(() => {
      if (attendanceCall?.queueStatus) {
        switch (attendanceCall?.queueStatus) {
          case ATTENDANCE_STATES.CALL_STARTED:
          case ATTENDANCE_STATES.CALL_RESTARTED:
            return false
        }
      }
    })

    return () => {
      unblock()
    }
  }, [
    history,
    realtimeAttendance?.immediate?.status,
    attendanceCall?.queueStatus,
  ])

  useEffect(() => {
    if (appointmentId) {
      useCallMonitoringStore.setState({
        appointmentId: parseInt(appointmentId),
      })
    }
  }, [appointmentId])

  return (
    <CustomLayout>
      <div css={styles.container}>
        <div>
          <Text variant="headingLarge">
            Videochamada com {attendanceCall?.professionalName}
          </Text>

          <ul css={styles.meetingInformations}>
            <li>
              <Text>
                Tipo de consulta:{' '}
                <span>
                  {realtimeAttendance?.isImmediate ? 'Imediata' : 'Agendada'}
                </span>
              </Text>
            </li>
            <li>
              <Text>
                Especialidade: <span>{attendanceCall?.specialityName}</span>
              </Text>
            </li>
          </ul>

          <RenderIf condition={canInvitePeople}>
            <Button css={styles.inviteGuestButton} onClick={handleInviteClick}>
              Convidar pessoas
            </Button>
          </RenderIf>
        </div>
        <MeetManager
          clinicId={clinic?.id}
          enableTranscription={enableTranscription}
          meetingUrl={meetingUrl}
          attendingId={attendanceCall?.attendanceId}
        />
      </div>

      <RenderIf condition={clinic?.flag?.PermiteCriacaoUrlConvidado}>
        <InviteToCallModal sessionId={sessionId} />
      </RenderIf>

      <CanceledByVideoErrorModal />
    </CustomLayout>
  )
}

export default validateRulesToAccessMeet(VideoRoom)
